import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';
import {
  MAANEDER, MAANED_KORT, INNTEKTER, FASTE_UTGIFTER, KREDITTKORT,
  GJENNOMFORT_TOM, AAR, budsjettSum, budsjettAar, netto, formatNOK
} from './data';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import CashFlow from './CashFlow';
import ReindekserKK from './ReindekserKK';
import KategoriTransaksjoner from './KategoriTransaksjoner';

const C = {
  bg: '#F0F2F5', surface: '#FFFFFF', surface2: '#E8ECF0',
  border: '#CBD2DA', text: '#1A2332', muted: '#6B7888',
  green: '#2D7D9A', greenL: '#E3F2F8', red: '#C0392B', redL: '#FBEAE8',
  blue: '#2C5F8A', blueL: '#E3EDF6', amber: '#7D6608', amberL: '#FEF9E7',
  dark: '#1A2332',
};

const MAANEDNAVN = ['Januar','Februar','Mars','April','Mai','Juni','Juli','August','September','Oktober','November','Desember'];

const KK_KATEGORIER = [
  'Dagligvarer', 'Reise og hotell', 'Elektronikk / digitalt',
  'Klær, hjem og interiør', 'Restaurant og kafe', 'Streaming / abonnement',
  'Drivstoff og parkering', 'Sport og aktivitet', 'Dyr / kjæledyr',
  'Hage og blomster', 'Helse', 'Trening', 'Hytte', 'Diverse øvrig (KK)',
];

const api = {
  login: (pin) => fetch('/api/login', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin })
  }).then(async r => ({ status: r.status, data: await r.json() })),

  getFaktisk: (token, aar) =>
    fetch(`/api/faktisk/${aar}`, { headers: { 'x-token': token } }).then(r => r.json()),

  hentGjennomfort: (token) =>
    fetch('/api/innstillinger/gjennomfort', { headers: { 'x-token': token } }).then(r => r.json()),

  settGjennomfort: (token, gjennomfortTom) =>
    fetch('/api/innstillinger/gjennomfort', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-token': token },
      body: JSON.stringify({ gjennomfortTom })
    }).then(r => r.json()),

  saveFaktisk: (token, data) => fetch('/api/faktisk', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-token': token },
    body: JSON.stringify(data)
  }).then(r => r.json()),

  logout: (token) => fetch('/api/logout', { method: 'POST', headers: { 'x-token': token } }),

  meg: (token) => fetch('/api/meg', { headers: { 'x-token': token } }).then(r => r.json()),

  webauthnMine: (token) => fetch('/api/auth/webauthn/mine', { headers: { 'x-token': token } }).then(r => r.json()),

  webauthnRegisterOptions: (token) => fetch('/api/auth/webauthn/register-options', {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'x-token': token }
  }).then(r => r.json()),

  webauthnRegisterVerify: (token, challengeId, response) => fetch('/api/auth/webauthn/register-verify', {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'x-token': token },
    body: JSON.stringify({ challengeId, response })
  }).then(r => r.json()),

  getPushTransaksjoner: (token, aar, maaned) =>
    fetch(`/api/push/transaksjoner?aar=${aar}&maaned=${maaned}`, {
      headers: { 'x-token': token }
    }).then(r => r.json()),

  slettPushTransaksjon: (token, id) =>
    fetch(`/api/push/transaksjon/${id}`, {
      method: 'DELETE', headers: { 'x-token': token }
    }).then(r => r.json()),

  oppdaterPushTransaksjon: (token, id, data) =>
    fetch(`/api/push/transaksjon/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'x-token': token },
      body: JSON.stringify(data)
    }).then(r => r.json()),

  overforTilBudsjett: (token, maaned, aar) =>
    fetch('/api/push/overfor', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-token': token },
      body: JSON.stringify({ maaned, aar })
    }).then(r => r.json()),

  importerDNB: (token, fil) => {
    const form = new FormData();
    form.append('fil', fil);
    return fetch('/api/import/dnb', {
      method: 'POST',
      headers: { 'x-token': token },
      body: form
    }).then(r => r.json());
  },

  hentLagrede: (token) =>
    fetch('/api/bank/lagrede', { headers: { 'x-token': token } }).then(r => r.json()),

  hentSynkVarsler: (token) =>
    fetch('/api/bank/synk-varsler', { headers: { 'x-token': token } }).then(r => r.json()),

  bankSynk: (token, konto) =>
    fetch(`/api/bank/synk${konto ? '?konto=' + konto : ''}`, {
      headers: { 'x-token': token }
    }).then(r => r.json()),

  autoSynk: (token) =>
    fetch('/api/bank/auto-synk', {
      method: 'POST',
      headers: { 'x-token': token }
    }).then(r => r.json()),

  bekreftImportAuto: (token, transaksjoner, overstyr = false) =>
    fetch('/api/import/bekreft-auto', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-token': token },
      body: JSON.stringify({ transaksjoner, overstyr })
    }).then(r => r.json()),

  omkategoriserLike: (token, butikk, kategori, dryRun = false) =>
    fetch('/api/push/omkategoriser-like', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-token': token },
      body: JSON.stringify({ butikk, kategori, dryRun })
    }).then(r => r.json()),

  lagreKategoriLæring: (token, nokkel, kategori) =>
    fetch('/api/kategorilaering', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-token': token },
      body: JSON.stringify({ nokkel, kategori })
    }).then(r => r.json()),
};

const card = (extra = {}) => ({
  background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: 10, padding: '18px 20px', ...extra
});
const mono = { fontFamily: "'DM Mono', monospace" };

function useIsMobile() {
  const [w, setW] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w <= 768;
}

// ══════════════════════════════════════════════════════════════════════════
// INNLOGGING (Face ID auto-trigger + PIN fallback)
// ══════════════════════════════════════════════════════════════════════════
//
// Tilstandsmaskin:
//   'init'         — sjekker WebAuthn-støtte og credential
//   'venterFaceID' — Face ID-dialog er åpnet av nettleseren
//   'feil'         — Face ID feilet/avbrutt, viser feilmelding + PIN-lenke
//   'pin'          — PIN-skjerm (med tilbake-knapp → prøv Face ID igjen)
//   'ingenCred'    — ingen credential funnet, viser PIN direkte
//
function Innlogging({ onLogin }) {
  const [tilstand, setTilstand] = useState('init');
  const [feilmelding, setFeilmelding] = useState(null);

  const fullforLogin = (token) => { sessionStorage.setItem('token', token); onLogin(token); };

  const startFaceID = useCallback(async () => {
    setTilstand('venterFaceID');
    setFeilmelding(null);
    try {
      const optsRes = await fetch('/api/auth/webauthn/login-options', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      }).then(r => r.json());
      if (optsRes.error) { setTilstand('ingenCred'); return; }

      const authResponse = await startAuthentication({ optionsJSON: optsRes.options });

      const res = await fetch('/api/auth/webauthn/login-verify', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId: optsRes.challengeId, response: authResponse }),
      }).then(r => r.json());

      if (res.token) fullforLogin(res.token);
      else { setFeilmelding(res.error || 'Verifisering feilet'); setTilstand('feil'); }
    } catch (e) {
      setFeilmelding(e.name === 'NotAllowedError' ? 'Face ID ble avbrutt' : e.message);
      setTilstand('feil');
    }
  }, []);

  // Kjør ved mount: sjekk støtte og credential, trigger Face ID automatisk
  useEffect(() => {
    if (!window.PublicKeyCredential) { setTilstand('ingenCred'); return; }
    fetch('/api/auth/webauthn/has-credential')
      .then(r => r.json())
      .then(d => (d.har && window.location.hostname === d.rpId) ? startFaceID() : setTilstand('ingenCred'))
      .catch(() => setTilstand('ingenCred'));
  }, [startFaceID]);

  const header = (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 11, letterSpacing: '0.2em', color: '#9AB8A8', textTransform: 'uppercase', marginBottom: 12, fontFamily: "'DM Sans', sans-serif" }}>Hatlen-Stokke</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#fff', fontFamily: "'Syne', sans-serif", letterSpacing: '-0.5px' }}>Familiebudsjett 2026</div>
    </div>
  );

  const pinLenke = (tekst, onClick) => (
    <button onClick={onClick} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: 13, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", marginTop: 8, padding: '8px 16px' }}>
      {tekst}
    </button>
  );

  // PIN-skjerm: tilbake-knapp bare hvis credential finnes (kan prøve Face ID igjen)
  if (tilstand === 'pin' || tilstand === 'ingenCred') {
    return (
      <PinSkjerm
        onLogin={fullforLogin}
        onTilbake={tilstand === 'pin' ? () => startFaceID() : null}
      />
    );
  }

  // Face ID-skjerm (init / venterFaceID / feil)
  return (
    <div style={{ minHeight: '100vh', background: C.dark, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24 }}>
      {header}

      <div style={{ minHeight: 24, textAlign: 'center' }}>
        {tilstand === 'venterFaceID' && (
          <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>
            Venter på Face ID…
          </div>
        )}
        {tilstand === 'feil' && feilmelding && (
          <div style={{ color: '#F0997B', fontSize: 13, fontFamily: "'DM Sans', sans-serif", maxWidth: 260 }}>
            {feilmelding}
          </div>
        )}
      </div>

      {pinLenke('Bruk PIN-kode', () => setTilstand('pin'))}
    </div>
  );
}

function PinSkjerm({ onLogin, onTilbake }) {
  const [digits, setDigits] = useState(['', '', '', '']);
  const [feil, setFeil] = useState(false);
  const [loading, setLoading] = useState(false);
  const refs = [React.useRef(), React.useRef(), React.useRef(), React.useRef()];

  const handleKey = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const ny = [...digits]; ny[i] = val; setDigits(ny); setFeil(null);
    if (val && i < 3) refs[i + 1].current.focus();
    if (val && i === 3) { const pin = [...ny.slice(0, 3), val].join(''); if (pin.length === 4) forsokLogin(pin); }
  };
  const handleBack = (i, e) => { if (e.key === 'Backspace' && !digits[i] && i > 0) refs[i - 1].current.focus(); };
  const forsokLogin = async (pin) => {
    setLoading(true);
    const { status, data } = await api.login(pin);
    setLoading(false);
    if (status === 200 && data.token) onLogin(data.token);
    else { setFeil(data.error || 'Feil PIN-kode. Prøv igjen.'); setDigits(['', '', '', '']); refs[0].current.focus(); }
  };
  useEffect(() => { refs[0].current?.focus(); }, []);

  return (
    <div style={{ minHeight: '100vh', background: C.dark, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 40 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.2em', color: '#9AB8A8', textTransform: 'uppercase', marginBottom: 12, fontFamily: "'DM Sans', sans-serif" }}>Hatlen-Stokke</div>
        <div style={{ fontSize: 28, fontWeight: 700, color: '#fff', fontFamily: "'Syne', sans-serif", letterSpacing: '-0.5px' }}>Familiebudsjett 2026</div>
      </div>
      <div style={{ display: 'flex', gap: 14 }}>
        {digits.map((d, i) => (
          <input key={i} ref={refs[i]} type="password" inputMode="numeric" maxLength={1} value={d}
            onChange={e => handleKey(i, e.target.value)} onKeyDown={e => handleBack(i, e)}
            style={{ width: 56, height: 64, textAlign: 'center', fontSize: 28, fontFamily: "'DM Mono', monospace", background: feil ? '#4A1B0C' : 'rgba(255,255,255,0.1)', border: `2px solid ${feil ? '#D85A30' : 'rgba(255,255,255,0.2)'}`, borderRadius: 12, color: '#fff', outline: 'none', transition: 'all 0.15s', caretColor: 'transparent' }} />
        ))}
      </div>
      {feil && <div style={{ color: '#F0997B', fontSize: 13 }}>{feil}</div>}
      {loading && <div style={{ color: '#9AB8A8', fontSize: 13 }}>Logger inn…</div>}
      {onTilbake && (
        <button onClick={onTilbake} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: 13, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", padding: '8px 16px' }}>
          ← Prøv Face ID igjen
        </button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// FACE ID — REGISTRERING AV NY ENHET (for allerede innlogget bruker)
// ══════════════════════════════════════════════════════════════════════════
function FaceIDSeksjon({ token }) {
  const [mine, setMine] = useState(null);
  const [registrerer, setRegistrerer] = useState(false);
  const [melding, setMelding] = useState(null);

  useEffect(() => { api.webauthnMine(token).then(setMine); }, [token]);

  const domenetStemmer = mine && window.location.hostname === mine.rpId;

  const registrer = async () => {
    setRegistrerer(true);
    setMelding(null);
    try {
      const optsRes = await api.webauthnRegisterOptions(token);
      if (optsRes.error) { setMelding({ ok: false, tekst: optsRes.error }); return; }

      const regResponse = await startRegistration({ optionsJSON: optsRes.options });
      const verifyRes = await api.webauthnRegisterVerify(token, optsRes.challengeId, regResponse);

      if (verifyRes.ok) {
        setMelding({ ok: true, tekst: 'Face ID registrert på denne enheten.' });
        api.webauthnMine(token).then(setMine);
      } else {
        setMelding({ ok: false, tekst: verifyRes.error || 'Registrering feilet' });
      }
    } catch (e) {
      setMelding({ ok: false, tekst: e.name === 'NotAllowedError' ? 'Registrering ble avbrutt' : e.message });
    } finally {
      setRegistrerer(false);
    }
  };

  return (
    <div style={{ padding: '10px 24px', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ fontSize: 12, letterSpacing: '0.05em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', marginBottom: 8 }}>Face ID</div>
      {mine && (
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 10 }}>
          {mine.antall > 0 ? `${mine.antall} registrert${mine.antall > 1 ? 'e' : ''} passnøkkel${mine.antall > 1 ? 'r' : ''} på denne kontoen.` : 'Ingen passnøkkel registrert på denne kontoen ennå.'}
        </div>
      )}
      {domenetStemmer ? (
        <button onClick={registrer} disabled={registrerer} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', padding: '8px 14px', borderRadius: 6, cursor: registrerer ? 'default' : 'pointer', fontSize: 13, fontFamily: "'DM Sans', sans-serif", opacity: registrerer ? 0.6 : 1 }}>
          {registrerer ? 'Registrerer…' : 'Registrer Face ID på denne enheten'}
        </button>
      ) : mine && (
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>Face ID er ikke aktivert for dette domenet ennå.</div>
      )}
      {melding && (
        <div style={{ marginTop: 8, fontSize: 12, color: melding.ok ? '#9AB8A8' : '#F0997B' }}>{melding.tekst}</div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// HOVED-APP
// ══════════════════════════════════════════════════════════════════════════
export default function App() {
  const [token, setToken] = useState(() => sessionStorage.getItem('token'));
  const [fane, setFane] = useState('oversikt');
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  React.useEffect(() => { const h = () => setWindowWidth(window.innerWidth); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  const isMobile = windowWidth <= 768;
  const [faktisk, setFaktisk] = useState([]);
  const [valgtMaaned, setValgtMaaned] = useState(() => {
    const naa = new Date();
    return naa.getFullYear() === AAR ? naa.getMonth() : 11;
  });
  const [redigerer, setRedigerer] = useState(null);
  const [lagrer, setLagrer] = useState(false);
  const [merMenyApen, setMerMenyApen] = useState(false);
  const [faceIdApen, setFaceIdApen] = useState(false);
  const [bruker, setBruker] = useState(null);
  const [gjennomfortTomServer, setGjennomfortTomServer] = useState(null);
  const [gjennomfortHentet, setGjennomfortHentet] = useState(false);
  const gjennomfortTom = gjennomfortTomServer ?? GJENNOMFORT_TOM;
  const [laasDialogApen, setLaasDialogApen] = useState(false);
  const [laaser, setLaaser] = useState(false);

  const lastFaktisk = useCallback(async () => {
    if (!token) return;
    const data = await api.getFaktisk(token, AAR);
    if (Array.isArray(data)) setFaktisk(data);
  }, [token]);

  useEffect(() => { lastFaktisk(); }, [lastFaktisk]);
  useEffect(() => { if (token) api.meg(token).then(d => setBruker(d.bruker || null)); }, [token]);
  useEffect(() => {
    if (!token) return;
    api.hentGjennomfort(token).then(d => {
      if (typeof d?.gjennomfortTom === 'number') setGjennomfortTomServer(d.gjennomfortTom);
      setGjennomfortHentet(true);
    });
  }, [token]);

  // Spør om å låse forrige måned fra og med den 5. i ny måned
  useEffect(() => {
    if (!token || !gjennomfortHentet) return;
    const naa = new Date();
    if (naa.getFullYear() !== AAR) return;
    if (naa.getDate() < 5) return;
    const inneværendeIndeks = naa.getMonth();
    if (gjennomfortTom >= inneværendeIndeks) return;
    if (sessionStorage.getItem('laasSpurtOkt') === 'true') return;
    setLaasDialogApen(true);
  }, [token, gjennomfortHentet, gjennomfortTom]);

  const laasMaaned = async () => {
    setLaaser(true);
    const nyVerdi = new Date().getMonth();
    const res = await api.settGjennomfort(token, nyVerdi);
    setLaaser(false);
    if (res.ok) {
      setGjennomfortTomServer(res.gjennomfortTom);
      setLaasDialogApen(false);
    }
  };

  const laasSenere = () => {
    sessionStorage.setItem('laasSpurtOkt', 'true');
    setLaasDialogApen(false);
  };

  const loggUt = useCallback(async () => { await api.logout(token); sessionStorage.removeItem('token'); setToken(null); }, [token]);

  useEffect(() => {
    if (!token) return;
    let timer;
    const reset = () => { clearTimeout(timer); timer = setTimeout(loggUt, 15 * 60 * 1000); };
    const events = ['mousemove', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, reset));
    reset();
    return () => { clearTimeout(timer); events.forEach(e => window.removeEventListener(e, reset)); };
  }, [token, loggUt]);

  const getFaktisk = (kategori, maaned) => { const r = faktisk.find(f => f.maaned === maaned + 1 && f.kategori === kategori); return r ? r.beloep : null; };
  const lagreFaktisk = async (kategori, maaned, beloep, notat) => {
    setLagrer(true);
    await api.saveFaktisk(token, { aar: AAR, maaned: maaned + 1, kategori, beloep: parseFloat(beloep) || 0, notat });
    await lastFaktisk(); setLagrer(false); setRedigerer(null);
  };

  if (!token) return <Innlogging onLogin={t => setToken(t)} />;

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: C.bg, minHeight: '100vh', color: C.text }}>
      {/* Topbar — desktop */}
      {!isMobile && <div style={{ background: C.dark, padding: '0 24px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 15, color: '#fff', letterSpacing: '-0.3px' }}>Hatlen-Stokke</div>
          <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.2)' }} />
          {[['oversikt','Oversikt'],['maaned','Måneder'],['kategorier','Kategorier'],['aarsprognose','Årsprognose'],['cashflow','Cash flow'],['bank','Bank'],['chat','💬 Chat']].map(([id, lbl]) => (
            <button key={id} onClick={() => setFane(id)} style={{ background: fane === id ? 'rgba(255,255,255,0.12)' : 'transparent', border: 'none', color: fane === id ? '#fff' : 'rgba(255,255,255,0.55)', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontFamily: "'DM Sans', sans-serif", fontWeight: fane === id ? 500 : 400 }}>{lbl}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative' }}>
          {bruker && <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}>Innlogget som {bruker}</span>}
          <button onClick={() => setFaceIdApen(v => !v)} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.5)', padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Face ID</button>
          <button onClick={loggUt} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.5)', padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Logg ut</button>
          {faceIdApen && <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 250 }} onClick={() => setFaceIdApen(false)} />
            <div style={{ position: 'absolute', top: 40, right: 0, width: 280, background: '#1A2332', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, zIndex: 251, boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>
              <FaceIDSeksjon token={token} />
            </div>
          </>}
        </div>
      </div>}
      {/* Topbar — mobil */}
      {isMobile && <div style={{ background: C.dark, padding: '0 16px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 16, color: '#fff' }}>Hatlen-Stokke</div>
        <button onClick={loggUt} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.5)', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>Logg ut</button>
      </div>}
      {isMobile && bruker && (
        <div style={{ background: C.dark, padding: '4px 16px 8px', textAlign: 'right' }}>
          <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontFamily: "'DM Sans', sans-serif" }}>Innlogget som {bruker}</span>
        </div>
      )}
      <div className={isMobile ? 'main-content-mobile' : ''} style={{ padding: isMobile ? undefined : '28px 28px', maxWidth: 1100, margin: '0 auto' }}>
        {fane === 'oversikt' && <Oversikt faktisk={faktisk} gjennomfortTom={gjennomfortTom} />}
        {fane === 'maaned' && <Maaneder valgt={valgtMaaned} setValgt={setValgtMaaned} faktisk={faktisk} getFaktisk={getFaktisk} redigerer={redigerer} setRedigerer={setRedigerer} lagreFaktisk={lagreFaktisk} lagrer={lagrer} gjennomfortTom={gjennomfortTom} />}
        {fane === 'kategorier' && <Kategorier token={token} faktisk={faktisk} getFaktisk={getFaktisk} gjennomfortTom={gjennomfortTom} />}
        {fane === 'aarsprognose' && <Aarsprognose faktisk={faktisk} getFaktisk={getFaktisk} gjennomfortTom={gjennomfortTom} />}
        {fane === 'cashflow' && <CashFlow token={token} getFaktisk={getFaktisk} gjennomfortTom={gjennomfortTom} />}
        {fane === 'bank' && <BankFane token={token} lastFaktisk={lastFaktisk} gjennomfortTom={gjennomfortTom} />}
        {fane === 'chat' && <ChatFane token={token} />}
      </div>
      {isMobile && <>
        {/* Mer-meny — åpnes over tabbaren */}
        {merMenyApen && <>
          <div style={{position:'fixed',inset:0,zIndex:201}} onClick={() => setMerMenyApen(false)} />
          <div style={{position:'fixed',bottom:'calc(60px + env(safe-area-inset-bottom, 0px))',left:0,right:0,background:'#1A2332',borderTop:'1px solid rgba(255,255,255,0.12)',zIndex:202}}>
            <div style={{padding:'10px 24px',fontSize:12,color:'rgba(255,255,255,0.35)',fontFamily:"'DM Sans', sans-serif"}}>{bruker ? `Innlogget som ${bruker}` : 'Innlogget'}</div>
            {[['kategorier','📊','Kategorier'],['aarsprognose','📈','Årsprognose'],['cashflow','💼','Cash flow']].map(([id,ikon,lbl]) => (
              <button key={id} onClick={() => { setFane(id); setMerMenyApen(false); }}
                style={{display:'flex',alignItems:'center',gap:14,width:'100%',background:fane===id?'rgba(255,255,255,0.08)':'none',border:'none',borderBottom:'1px solid rgba(255,255,255,0.07)',color:'#fff',padding:'16px 24px',fontSize:15,cursor:'pointer',fontFamily:"'DM Sans', sans-serif",textAlign:'left',boxSizing:'border-box'}}>
                <span style={{fontSize:20}}>{ikon}</span>
                <span style={{fontWeight:fane===id?600:400}}>{lbl}</span>
              </button>
            ))}
            <div style={{borderTop:'1px solid rgba(255,255,255,0.07)'}}>
              <FaceIDSeksjon token={token} />
            </div>
          </div>
        </>}
        {/* Tabbar — 4 hovedfaner + Mer */}
        <div className="tabbar-bottom" style={{position:'fixed',bottom:0,left:0,right:0,background:'#1A2332',borderTop:'1px solid rgba(255,255,255,0.1)',display:'flex',zIndex:200,minHeight:60,boxSizing:'border-box'}}>
          {[['oversikt','🏠','Oversikt'],['maaned','📅','Måneder'],['bank','🏦','Bank'],['chat','💬','Chat']].map(([id,ikon,lbl]) => (
            <button key={id} onClick={() => { setFane(id); setMerMenyApen(false); }}
              style={{flex:1,maxWidth:'20%',background:'none',border:'none',color:fane===id?'#fff':'rgba(255,255,255,0.45)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',cursor:'pointer',padding:'6px 2px',minHeight:44,gap:2}}>
              <span style={{fontSize:20,lineHeight:1}}>{ikon}</span>
              <span style={{fontSize:11,fontWeight:fane===id?600:400,fontFamily:"'DM Sans', sans-serif",whiteSpace:'nowrap'}}>{lbl}</span>
            </button>
          ))}
          <button onClick={() => setMerMenyApen(v => !v)}
            style={{flex:1,maxWidth:'20%',background:'none',border:'none',color:merMenyApen||['kategorier','aarsprognose','cashflow'].includes(fane)?'#fff':'rgba(255,255,255,0.45)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',cursor:'pointer',padding:'6px 2px',minHeight:44,gap:2}}>
            <span style={{fontSize:20,lineHeight:1}}>⋯</span>
            <span style={{fontSize:11,fontWeight:merMenyApen||['kategorier','aarsprognose','cashflow'].includes(fane)?600:400,fontFamily:"'DM Sans', sans-serif",whiteSpace:'nowrap'}}>Mer</span>
          </button>
        </div>
      </>}

      {/* Låsespørsmål — vises fra den 5. i ny måned hvis forrige måned ikke er låst ennå */}
      {laasDialogApen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(26,35,50,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: C.surface, borderRadius: 12, width: '100%', maxWidth: 400, padding: '18px 20px' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
              {MAANEDNAVN[gjennomfortTom]} er ferdig
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>
              Lås til og med {MAANEDNAVN[gjennomfortTom].toLowerCase()} som gjennomført?
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={laasMaaned} disabled={laaser} style={{ flex: 1, background: C.green, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 12px', fontSize: 13, fontWeight: 600, cursor: laaser ? 'default' : 'pointer', minWidth: 100 }}>
                {laaser ? 'Låser…' : 'Lås'}
              </button>
              <button onClick={laasSenere} style={{ flex: 1, background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', minWidth: 100 }}>
                Senere
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// PUSH-TRANSAKSJONER
// ══════════════════════════════════════════════════════════════════════════
function PushTransaksjoner({ token, lastFaktisk, gjennomfortTom }) {
  const nå = new Date();
  const [valgtMaaned, setValgtMaaned] = useState(nå.getMonth() + 1);
  const [valgtAar] = useState(nå.getFullYear());
  const [transaksjoner, setTransaksjoner] = useState([]);
  const [laster, setLaster] = useState(false);
  const [overforer, setOverforer] = useState(false);
  const [melding, setMelding] = useState(null);
  const [redigererKat, setRedigererKat] = useState(null);
  const [importData, setImportData] = useState(null);
  const [importerer, setImporterer] = useState(false);
  const [visImport, setVisImport] = useState(false);
  // Opprydningsmodus: vis én kategori på tvers av alle måneder, størst beløp først
  const [filterKategori, setFilterKategori] = useState('');
  // Masseomkategoriserings-dialog (Oppdater alle / Bare denne / Avbryt)
  const [masseDialog, setMasseDialog] = useState(null);

  const last = useCallback(async () => {
    setLaster(true);
    const data = filterKategori
      ? await fetch(`/api/push/transaksjoner?aar=${valgtAar}&kategori=${encodeURIComponent(filterKategori)}`, { headers: { 'x-token': token } }).then(r => r.json())
      : await api.getPushTransaksjoner(token, valgtAar, valgtMaaned);
    if (Array.isArray(data)) setTransaksjoner(filterKategori ? [...data].sort((a, b) => b.beloep - a.beloep) : data);
    setLaster(false);
  }, [token, valgtAar, valgtMaaned, filterKategori]);

  useEffect(() => { last(); }, [last]);

  const slett = async (id) => {
    if (!window.confirm('Slett denne transaksjonen?')) return;
    const r = await api.slettPushTransaksjon(token, id);
    if (r?.resummert) await lastFaktisk();
    if (r?.dekningsavvik) {
      setMelding({ type: 'advarsel', tekst: `Slettet, men månedstallene ble ikke oppdatert (avvik ${formatNOK(r.dekningsavvik)} mot rådata) — importer DNB Excel på nytt for denne måneden.` });
    }
    last();
  };

  const visKortMelding = (tekst) => {
    setMelding({ type: 'ok', tekst });
    setTimeout(() => setMelding(m => m?.tekst === tekst ? null : m), 6000);
  };

  const oppdaterKategori = async (id, kategori) => {
    const rad = transaksjoner.find(t => t.id === id);
    const butikk = rad?.butikk;
    const gammelKategori = rad?.kategori;
    const r = await api.oppdaterPushTransaksjon(token, id, { kategori });
    setRedigererKat(null);
    if (!r.ok) { setMelding({ type: 'feil', tekst: r.error || 'Oppdatering feilet' }); return; }
    if (r.resummert) await lastFaktisk();
    if (r.dekningsavvik) {
      setMelding({ type: 'advarsel', tekst: `Kategori endret, men månedstallene ble ikke oppdatert (avvik ${formatNOK(r.dekningsavvik)} mot rådata) — importer DNB Excel på nytt for denne måneden.` });
    }
    // Dry-run: finnes flere lagrede transaksjoner med samme normaliserte nøkkel?
    let dialogVist = false;
    if (butikk) {
      const d = await api.omkategoriserLike(token, butikk, kategori, true);
      if (d.ok && d.antall > 0) {
        setMasseDialog({ butikk, kategori, gammelKategori, id, antall: d.antall, navn: d.navn || [], nokkel: d.nokkel, unikeNavn: d.unikeNavn });
        dialogVist = true;
      }
    }
    if (!dialogVist && r.laert && !r.dekningsavvik) {
      visKortMelding(`Husker: ${butikk || r.laert.nokkel} → ${r.laert.kategori}${r.resummert ? ' · månedstallene er oppdatert' : ''}`);
    }
    last();
  };

  // Masse-dialog: Oppdater alle / Bare denne / Avbryt (angre)
  const masseOppdaterAlle = async () => {
    const d = masseDialog; setMasseDialog(null);
    const r = await api.omkategoriserLike(token, d.butikk, d.kategori, false);
    if (r.ok) {
      visKortMelding(`Oppdaterte ${r.antall + 1} transaksjoner (nøkkel «${r.nokkel}») → ${d.kategori}${r.maanederResummert ? ` · ${r.maanederResummert} måned(er) resummert` : ''}`);
      await lastFaktisk();
    } else setMelding({ type: 'feil', tekst: r.error || 'Masseoppdatering feilet' });
    last();
  };
  const masseBareDenne = () => {
    const d = masseDialog; setMasseDialog(null);
    visKortMelding(`Husker: ${d.butikk} → ${d.kategori} (kun denne transaksjonen endret)`);
  };
  const masseAvbryt = async () => {
    const d = masseDialog; setMasseDialog(null);
    // Angre: sett den ene transaksjonen (og læringen) tilbake til gammel kategori
    if (d.gammelKategori) {
      await api.oppdaterPushTransaksjon(token, d.id, { kategori: d.gammelKategori });
      visKortMelding(`Angret — ${d.butikk} står som ${d.gammelKategori}`);
      await lastFaktisk();
      last();
    }
  };

  const bekreft = async (id) => {
    await api.oppdaterPushTransaksjon(token, id, { bekreftet: true });
    last();
  };

  const overforTilBudsjett = async () => {
    if (!window.confirm(`Overfør alle transaksjoner for ${MAANED_KORT[valgtMaaned - 1]} til budsjett?`)) return;
    setOverforer(true);
    const res = await api.overforTilBudsjett(token, valgtMaaned, valgtAar);
    setOverforer(false);
    if (res.ok) {
      const kategorier = Object.entries(res.kategorier).map(([k, v]) => `${k}: ${formatNOK(v)}`).join(', ');
      setMelding({ type: 'ok', tekst: `Overført! ${kategorier}` });
      await lastFaktisk();
      last();
    } else {
      setMelding({ type: 'feil', tekst: res.error || 'Noe gikk galt' });
    }
  };

  const lastOppExcel = async (e) => {
    const fil = e.target.files[0];
    if (!fil) return;
    setImporterer(true);
    setMelding(null);
    setImportData(null);
    const res = await api.importerDNB(token, fil);
    setImporterer(false);
    if (res.ok) {
      setImportData(res);
      setVisImport(true);
      setMelding({ type: 'ok', tekst: `Leste ${res.raderLest ?? '?'} rader fra filen — fant ${res.antall} transaksjoner (${res.hoppetOver ?? 0} hoppet over: innbetalinger o.l.) · ${res.matchet} matcher push-registreringer, ${res.ny} nye` });
    } else {
      setMelding({ type: 'feil', tekst: res.error || 'Importfeil' });
    }
  };

  const bekreftImport = async () => {
    if (!importData) return;
    const maaneder = [...new Set(importData.transaksjoner.map(t => t.maaned))];
    const aar = importData.transaksjoner[0]?.aar;
    const låsteMaaneder = maaneder.filter(m => m <= gjennomfortTom); // gjennomførte måneder
    const advarsel = låsteMaaneder.length > 0
      ? `NB: Filen inneholder transaksjoner for ${låsteMaaneder.map(m => MAANEDER[m-1]).join(', ')} — gjennomførte måneder. Nye rader flettes inn (duplikater hoppes over) og månedssummene beregnes på nytt. Fortsett?`
      : `Bekreft import av ${importData.antall} transaksjoner?`;
    if (!window.confirm(advarsel)) return;
    setImporterer(true);
    let res = await api.bekreftImportAuto(token, importData.transaksjoner);
    // Rimelighetssjekk: store avvik mot eksisterende månedssummer krever bevisst bekreftelse
    if (res.krevBekreftelse) {
      const detaljer = res.avvik
        .map(a => `${MAANEDER[a.maaned - 1]} ${a.aar}: ${formatNOK(a.gammelSum)} → ${formatNOK(a.nySum)} (${a.prosent > 0 ? '+' : ''}${a.prosent} %)`)
        .join('\n');
      if (window.confirm(`⚠ Ny månedssum avviker mer enn 30 % fra eksisterende:\n\n${detaljer}\n\nFortsette og skrive de nye tallene likevel?`)) {
        res = await api.bekreftImportAuto(token, importData.transaksjoner, true);
      } else {
        setImporterer(false);
        setMelding({ type: 'feil', tekst: 'Import avbrutt — ingenting ble skrevet.' });
        return;
      }
    }
    setImporterer(false);
    if (!res.ok) {
      setMelding({ type: 'feil', tekst: res.error || 'Importen feilet' });
      return;
    }
    setImportData(null);
    setVisImport(false);
    setMelding({ type: 'ok', tekst: `Import bekreftet: ${res.nyeRader ?? '?'} nye rader, ${res.duplikater ?? 0} duplikater hoppet over. Faktiske tall er oppdatert.` });
    await lastFaktisk();
    last();
  };

  const endreImportKategori = async (idx, nyKategori) => {
    const butikk = importData.transaksjoner[idx]?.butikk;
    setImportData(prev => {
      const nye = [...prev.transaksjoner];
      nye[idx] = { ...nye[idx], kategori: nyKategori };
      return { ...prev, transaksjoner: nye };
    });
    if (butikk) {
      try {
        const res = await api.lagreKategoriLæring(token, butikk, nyKategori);
        if (!res.ok && !res.ignorert) setMelding({ type: 'feil', tekst: 'Kategori endret, men læring ble ikke lagret.' });
      } catch {
        setMelding({ type: 'feil', tekst: 'Kategori endret, men læring ble ikke lagret (nettverksfeil).' });
      }
    }
  };

  const sammendrag = transaksjoner.reduce((acc, t) => { acc[t.kategori] = (acc[t.kategori] || 0) + t.beloep; return acc; }, {});
  const totalBeloep = transaksjoner.reduce((s, t) => s + t.beloep, 0);
  const ubekreftet = transaksjoner.filter(t => !t.bekreftet).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px', margin: 0 }}>Kredittkorttransaksjoner</h1>
          <p style={{ color: C.muted, fontSize: 13, margin: '4px 0 0' }}>Registrert via DNB Excel-import · {ubekreftet > 0 ? `${ubekreftet} ubekreftet` : 'Alle bekreftet'}</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          <div className="month-scroll" style={{ maxWidth: '100%', opacity: filterKategori ? 0.35 : 1, pointerEvents: filterKategori ? 'none' : 'auto' }}>
            {MAANED_KORT.map((m, i) => (
              <button key={i} onClick={() => setValgtMaaned(i + 1)} style={{ padding: '8px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', background: valgtMaaned === i + 1 ? C.dark : C.surface, color: valgtMaaned === i + 1 ? '#fff' : C.muted, border: `1px solid ${valgtMaaned === i + 1 ? C.dark : C.border}`, whiteSpace: 'nowrap', minHeight: 36, flexShrink: 0 }}>{m}</button>
            ))}
            <button onClick={last} style={{ background: C.surface, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 12px', fontSize: 12, cursor: 'pointer', minHeight: 36, flexShrink: 0 }}>↻</button>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <label className="btn-primary" style={{ background: C.blue, color: '#fff', border: 'none', borderRadius: 6, padding: '0 14px', fontSize: 13, cursor: 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center' }}>
              {importerer ? 'Leser fil...' : '📂 Last opp Excel'}
              <input type="file" accept=".xlsx,.xls" onChange={lastOppExcel} style={{ display: 'none' }} />
            </label>
            {transaksjoner.length > 0 && (
              <button onClick={overforTilBudsjett} disabled={overforer} className="btn-primary" style={{ background: C.green, color: '#fff', border: 'none', borderRadius: 6, padding: '0 14px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                {overforer ? 'Overfører...' : '→ Overfør til budsjett'}
              </button>
            )}
          </div>
        </div>
      </div>

      {melding && (
        <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14, background: melding.type === 'ok' ? C.greenL : melding.type === 'advarsel' ? C.amberL : C.redL, borderLeft: `3px solid ${melding.type === 'ok' ? C.green : melding.type === 'advarsel' ? C.amber : C.red}`, color: melding.type === 'ok' ? C.green : melding.type === 'advarsel' ? C.amber : C.red }}>
          {melding.tekst}
        </div>
      )}

      {/* Import forhåndsvisning */}
      {visImport && importData && (
        <div style={{ ...card(), marginBottom: 20, borderTop: `3px solid ${C.blue}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.blue }}>Excel-import forhåndsvisning</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                Leste {importData.raderLest ?? '?'} rader · {importData.antall} transaksjoner · {importData.matchet} matcher eksisterende · {importData.ny} nye
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setVisImport(false); setImportData(null); }} style={{ background: C.surface, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 12px', fontSize: 12, cursor: 'pointer' }}>Avbryt</button>
              <button onClick={bekreftImport} disabled={importerer} style={{ background: C.blue, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                {importerer ? 'Importerer...' : `✓ Bekreft import av ${importData.antall} transaksjoner`}
              </button>
            </div>
          </div>
          {/* Fordeling per kategori */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {Object.entries(importData.transaksjoner.reduce((acc, t) => { acc[t.kategori] = (acc[t.kategori] || 0) + t.beloep; return acc; }, {}))
              .sort((a, b) => b[1] - a[1])
              .map(([kat, sum]) => (
              <div key={kat} style={{ background: C.blueL, borderRadius: 8, padding: '6px 12px', fontSize: 11 }}>
                <div style={{ color: C.blue, marginBottom: 2 }}>{kat}</div>
                <div style={{ ...mono, fontWeight: 600, fontSize: 12 }}>{formatNOK(sum)}</div>
              </div>
            ))}
          </div>
          {/* Transaksjoner */}
          <div className="scroll-x" style={{ maxHeight: 300, overflowY: 'auto', fontSize: 11 }}>
            <div style={{ minWidth: 480, display: 'grid', gridTemplateColumns: '70px 1fr 1.5fr 80px 60px', fontWeight: 600, color: C.muted, padding: '4px 6px', borderBottom: `1px solid ${C.border}`, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {['Dato', 'Butikk', 'Kategori', 'Beløp', 'Status'].map((h, i) => <div key={i} style={{ textAlign: i === 3 ? 'right' : 'left' }}>{h}</div>)}
            </div>
            {importData.transaksjoner.slice(0, 100).map((t, i) => (
              <div key={i} style={{ minWidth: 480, display: 'grid', gridTemplateColumns: '70px 1fr 1.8fr 80px 60px', padding: '5px 6px', borderBottom: `1px solid ${C.border}`, background: t.status === 'matchet' ? C.greenL : 'transparent', alignItems: 'center' }}>
                <div style={{ color: C.muted, fontSize: 11 }}>{t.datoStr?.slice(5).replace('-', '.')}</div>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}>{t.butikk}</div>
                <select
                  value={t.kategori}
                  onChange={e => endreImportKategori(i, e.target.value)}
                  style={{ padding: '2px 6px', borderRadius: 5, border: `1px solid ${C.border}`, fontSize: 11, background: '#fff', cursor: 'pointer', width: '100%' }}
                >
                  {KK_KATEGORIER.map(k => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
                <div style={{ ...mono, textAlign: 'right', fontWeight: 500, fontSize: 11 }}>{formatNOK(t.beloep)}</div>
                <div style={{ textAlign: 'right', color: t.status === 'matchet' ? C.green : C.blue, fontSize: 10 }}>
                  {t.status === 'matchet' ? '✓ match' : 'ny'}
                </div>
              </div>
            ))}
            {importData.antall > 50 && <div style={{ textAlign: 'center', padding: 8, color: C.muted }}>Viser 50 av {importData.antall}</div>}
          </div>
        </div>
      )}

      {/* KPI-kort */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <div style={{ ...card(), borderTop: `3px solid ${C.amber}` }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>{filterKategori ? `Totalt i ${filterKategori}` : 'Totalt denne måneden'}</div>
          <div style={{ ...mono, fontSize: 22, fontWeight: 600, color: C.amber }}>{formatNOK(totalBeloep)}</div>
        </div>
        <div style={{ ...card(), borderTop: `3px solid ${C.blue}` }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Antall transaksjoner</div>
          <div style={{ ...mono, fontSize: 22, fontWeight: 600, color: C.blue }}>{transaksjoner.length}</div>
        </div>
        <div style={{ ...card(), borderTop: `3px solid ${ubekreftet > 0 ? C.amber : C.green}` }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Status</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: ubekreftet > 0 ? C.amber : C.green }}>
            {ubekreftet > 0 ? `${ubekreftet} ubekreftet` : '✓ Alle bekreftet'}
          </div>
        </div>
      </div>

      {/* Sammendrag per kategori */}
      {Object.keys(sammendrag).length > 0 && (
        <div style={{ ...card(), marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 12 }}>Fordeling per kategori</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {Object.entries(sammendrag).sort((a, b) => b[1] - a[1]).map(([kat, sum]) => (
              <div key={kat} style={{ background: C.surface2, borderRadius: 8, padding: '8px 14px', fontSize: 12 }}>
                <div style={{ color: C.muted, fontSize: 11, marginBottom: 2 }}>{kat}</div>
                <div style={{ ...mono, fontWeight: 600 }}>{formatNOK(sum)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transaksjonsliste */}
      <div style={card()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {filterKategori ? `${filterKategori} — hele ${valgtAar} · størst først` : `Transaksjoner — ${MAANED_KORT[valgtMaaned - 1]}`}
          </div>
          <select
            value={filterKategori}
            onChange={e => setFilterKategori(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${filterKategori ? C.blue : C.border}`, fontSize: 12, background: filterKategori ? C.blueL : C.surface, color: filterKategori ? C.blue : C.text, cursor: 'pointer', maxWidth: 260 }}
          >
            <option value="">Filtrer på kategori (alle måneder)…</option>
            {KK_KATEGORIER.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>

        {laster && <div style={{ textAlign: 'center', padding: 20, color: C.muted, fontSize: 13 }}>Laster...</div>}

        {!laster && transaksjoner.length === 0 && (
          <div style={{ textAlign: 'center', padding: 32, color: C.muted, fontSize: 13 }}>
            {filterKategori
              ? `Ingen transaksjoner i ${filterKategori} for ${valgtAar}. 🎉`
              : <>Ingen transaksjoner registrert for {MAANED_KORT[valgtMaaned - 1]}.<br /><span style={{ fontSize: 12, marginTop: 8, display: 'block' }}>Last opp en DNB Excel-eksport for å legge inn transaksjoner.</span></>}
          </div>
        )}

        {transaksjoner.length > 0 && (
          <>
            <div className="scroll-x">
            <div style={{ minWidth: 520, display: 'grid', gridTemplateColumns: '90px 1fr 1.8fr 1fr 110px', fontSize: 10, fontWeight: 600, color: C.muted, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '6px 8px', borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
              {['Dato', 'Butikk', 'Kategori', 'Beløp', ''].map((h, i) => (
                <div key={i} style={{ textAlign: i === 3 ? 'right' : 'left' }}>{h}</div>
              ))}
            </div>

            {transaksjoner.map((t, i) => (
              <div key={t.id} style={{ minWidth: 520, display: 'grid', gridTemplateColumns: '90px 1fr 1.8fr 1fr 110px', alignItems: 'center', gap: 8, padding: '9px 8px', background: i % 2 === 0 ? 'transparent' : C.surface2, borderBottom: `1px solid ${C.border}`, borderLeft: t.bekreftet ? 'none' : `3px solid ${C.amber}` }}>
                <div style={{ fontSize: 11, color: C.muted }}>
                  {new Date(t.opprettet).toLocaleDateString('no-NO', { day: '2-digit', month: '2-digit' })}
                </div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  {t.butikk}
                  {!t.bekreftet && <span style={{ marginLeft: 8, fontSize: 10, color: C.amber, background: C.amberL, padding: '1px 6px', borderRadius: 10 }}>ubekreftet</span>}
                </div>
                <div>
                  {redigererKat === t.id ? (
                    <select defaultValue={t.kategori} onChange={e => oppdaterKategori(t.id, e.target.value)} onBlur={() => setRedigererKat(null)} autoFocus
                      style={{ padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.border}`, fontSize: 12, background: '#fff', cursor: 'pointer', width: '100%' }}>
                      {KK_KATEGORIER.map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  ) : (
                    <span onClick={() => setRedigererKat(t.id)} style={{ fontSize: 11, color: C.blue, background: C.blueL, padding: '2px 8px', borderRadius: 10, cursor: 'pointer', display: 'inline-block' }} title="Klikk for å endre">{t.kategori}</span>
                  )}
                </div>
                <div style={{ ...mono, fontSize: 13, fontWeight: 600, textAlign: 'right', color: C.amber }}>{formatNOK(t.beloep)}</div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  {!t.bekreftet && (
                    <button onClick={() => bekreft(t.id)} style={{ background: C.greenL, color: C.green, border: `1px solid ${C.green}`, borderRadius: 5, padding: '3px 8px', fontSize: 10, cursor: 'pointer' }}>✓</button>
                  )}
                  <button onClick={() => slett(t.id)} style={{ background: C.redL, color: C.red, border: `1px solid ${C.red}`, borderRadius: 5, padding: '3px 8px', fontSize: 10, cursor: 'pointer' }}>✕</button>
                </div>
              </div>
            ))}

            <div style={{ minWidth: 520, display: 'grid', gridTemplateColumns: '90px 1fr 1.8fr 1fr 110px', padding: '10px 8px', background: C.dark, borderRadius: 6, marginTop: 8 }}>
              <div style={{ fontSize: 12, color: '#fff', fontWeight: 600, gridColumn: '1/4' }}>Totalt {transaksjoner.length} transaksjoner</div>
              <div style={{ ...mono, fontSize: 13, fontWeight: 600, color: '#fff', textAlign: 'right' }}>{formatNOK(totalBeloep)}</div>
              <div />
            </div>
            </div>{/* /scroll-x */}
          </>
        )}
      </div>

      <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: C.amberL, borderLeft: `3px solid ${C.amber}`, fontSize: 12, color: C.amber }}>
        <strong>Tips:</strong> Klikk på kategori-etiketten for å endre. Trykk "→ Overfør til budsjett" for å oppdatere faktiske tall i Måneder-fanen.
      </div>

      <ReindekserKK token={token} lastFaktisk={lastFaktisk} />

      {/* Masseomkategoriserings-dialog — viser nøkkel og butikknavnene som treffes */}
      {masseDialog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(26,35,50,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: C.surface, borderRadius: 12, width: '100%', maxWidth: 440, padding: '18px 20px' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
              {masseDialog.antall} andre transaksjoner fra samme butikk
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
              Matcher normalisert nøkkel «{masseDialog.nokkel}» · oppdatere alle til <strong>{masseDialog.kategori}</strong>?
            </div>
            <div style={{ background: C.surface2, borderRadius: 8, padding: '8px 12px', marginBottom: 14, maxHeight: 160, overflowY: 'auto' }}>
              {masseDialog.navn.map(n => (
                <div key={n.butikk} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.butikk}</span>
                  <span style={{ color: C.muted, flexShrink: 0, marginLeft: 8 }}>× {n.antall}</span>
                </div>
              ))}
              {masseDialog.unikeNavn > masseDialog.navn.length && (
                <div style={{ fontSize: 11, color: C.muted, paddingTop: 4 }}>… og {masseDialog.unikeNavn - masseDialog.navn.length} flere navnevarianter</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={masseOppdaterAlle} style={{ flex: 1, background: C.green, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', minWidth: 130 }}>
                Oppdater alle ({masseDialog.antall})
              </button>
              <button onClick={masseBareDenne} style={{ flex: 1, background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', minWidth: 100 }}>
                Bare denne
              </button>
              <button onClick={masseAvbryt} style={{ background: 'transparent', color: C.red, border: `1px solid ${C.red}`, borderRadius: 8, padding: '10px 12px', fontSize: 13, cursor: 'pointer' }}>
                Avbryt (angre)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// OVERSIKT
// ══════════════════════════════════════════════════════════════════════════
function Oversikt({ faktisk, gjennomfortTom }) {
  const innAar = budsjettAar(INNTEKTER);
  const fastAar = budsjettAar(FASTE_UTGIFTER);
  const kkAar = budsjettAar(KREDITTKORT);
  const nettoAar = innAar - fastAar - kkAar;
  const chartData = MAANEDER.map((m, i) => ({ name: MAANED_KORT[i], netto: Math.round(netto(i)), gjennomfort: i < gjennomfortTom }));
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const v = payload[0].value;
    return <div style={{ ...card(), padding: '10px 14px', fontSize: 12 }}><div style={{ color: C.muted, marginBottom: 4 }}>{label}</div><div style={{ ...mono, fontWeight: 600, color: v >= 0 ? C.green : C.red }}>{formatNOK(v, true)}</div></div>;
  };
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px', margin: 0 }}>Oversikt 2026</h1>
        <p style={{ color: C.muted, fontSize: 13, margin: '4px 0 0' }}>Budsjettår {AAR} · Jan–{MAANEDER[gjennomfortTom - 1].toLowerCase()} er gjennomført</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { lbl: 'Årsbudsjett inntekt', val: innAar, sub: 'inkl. feriepenger jun', pos: true },
          { lbl: 'Årsbudsjett utgifter', val: fastAar + kkAar, sub: 'fast + kredittkort', pos: false, forceBlue: true },
          { lbl: 'Budsjettert årsresultat', val: nettoAar, sub: 'inkl. feriepenger', pos: nettoAar > 0 },
          { lbl: 'Snitt månedsnetto', val: Math.round(nettoAar / 12), sub: `ordinær mnd ≈ ${formatNOK(Math.round(netto(8)), true)}`, pos: nettoAar > 0 },
        ].map((k, i) => (
          <div key={i} style={{ ...card(), borderTop: `3px solid ${k.forceBlue ? C.blue : k.pos ? C.green : C.red}` }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, letterSpacing: '0.03em', marginBottom: 6 }}>{k.lbl}</div>
            <div style={{ ...mono, fontSize: window.innerWidth <= 768 ? 16 : 20, fontWeight: 600, color: k.forceBlue ? C.blue : k.pos ? C.green : C.red }}>{formatNOK(k.val)}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{k.sub}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '2fr 1fr', gap: 12 }}>
        <div style={card()}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: C.muted, marginBottom: 14 }}>Månedlig netto — budsjett</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} barSize={20}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} tickFormatter={v => `${Math.round(v / 1000)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={0} stroke={C.border} />
              <Bar dataKey="netto" radius={[3, 3, 0, 0]}>
                {chartData.map((d, i) => <Cell key={i} fill={d.netto >= 0 ? (d.gjennomfort ? '#9FE1CB' : C.green) : C.red} opacity={d.gjennomfort ? 0.7 : 1} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={card()}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: C.muted, marginBottom: 14 }}>Årsfordeling</div>
          {[
            { lbl: 'Inntekter', val: innAar, col: C.green },
            { lbl: '— Bolig + leasing', val: -(budsjettAar({ a: FASTE_UTGIFTER['Boliglån — renter'], b: FASTE_UTGIFTER['Billeasing (DNB Finans)'] })), col: C.blue },
            { lbl: '— Forsikring', val: -(budsjettAar({ a: FASTE_UTGIFTER['Forsikring'] })), col: C.blue },
            { lbl: '— Øvrige faste', val: -(fastAar - budsjettAar({ a: FASTE_UTGIFTER['Boliglån — renter'], b: FASTE_UTGIFTER['Billeasing (DNB Finans)'], c: FASTE_UTGIFTER['Forsikring'] })), col: C.blue },
            { lbl: '— Kredittkort', val: -kkAar, col: C.amber },
            { lbl: 'Årsresultat', val: nettoAar, col: nettoAar > 0 ? C.green : C.red, bold: true },
          ].map((r, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: i < 5 ? `1px solid ${C.border}` : 'none', borderTop: r.bold ? `2px solid ${C.green}` : 'none', marginTop: r.bold ? 4 : 0 }}>
              <span style={{ fontSize: 12, fontWeight: r.bold ? 600 : 400 }}>{r.lbl}</span>
              <span style={{ ...mono, fontSize: 12, fontWeight: r.bold ? 600 : 400, color: r.col }}>{formatNOK(r.val)}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: C.amberL, borderLeft: `3px solid ${C.amber}`, fontSize: 12, color: C.amber }}>
        <strong>Jan 2026</strong> viser stort minus ({formatNOK(Math.round(netto(0)), true)}) pga. store reisekjøp — Reise og hotell {formatNOK(KREDITTKORT['Reise og hotell'].budsjett[0])} den måneden. <strong>Jun</strong> løftes av feriepenger.
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MÅNEDER
// ══════════════════════════════════════════════════════════════════════════
function Maaneder({ valgt, setValgt, faktisk, getFaktisk, redigerer, setRedigerer, lagreFaktisk, lagrer, gjennomfortTom }) {
  const isMobile = useIsMobile();
  const erGjennomfort = valgt < gjennomfortTom;
  const innSum = budsjettSum(INNTEKTER, valgt);
  const fastSum = budsjettSum(FASTE_UTGIFTER, valgt);
  const kkSum = budsjettSum(KREDITTKORT, valgt);
  const nettoVal = innSum - fastSum - kkSum;
  // Toppkortene viser faktisk der det finnes (budsjett som fallback per
  // kategori) — samme mønster som Årsprognose og Cash flow
  const summerFaktisk = (obj) => Object.entries(obj).reduce((s, [k, v]) => {
    const f = getFaktisk(k, valgt);
    return s + (f !== null ? f : (v.budsjett[valgt] || 0));
  }, 0);
  const innFakt = Math.round(summerFaktisk(INNTEKTER));
  const fastFakt = Math.round(summerFaktisk(FASTE_UTGIFTER));
  const kkFakt = Math.round(summerFaktisk(KREDITTKORT));
  const nettoFakt = innFakt - fastFakt - kkFakt;
  const [editVal, setEditVal] = useState('');
  const [editNotat, setEditNotat] = useState('');
  const startRediger = (kat, budj, f) => { setEditVal(f !== null ? String(f) : String(budj)); setEditNotat(''); setRedigerer(kat); };
  const lagre = (kat) => lagreFaktisk(kat, valgt, editVal, editNotat);

  const RenderRad = ({ kat, budj, erInntekt }) => {
    const f = getFaktisk(kat, valgt);
    const erRed = redigerer === kat;
    const avvik = f !== null ? f - budj : null;
    // Inntekter: positivt avvik (mer enn budsjett) = grønt. Utgifter: positivt avvik = rødt.
    const avvikGod = avvik !== null && (erInntekt ? avvik >= 0 : avvik <= 0);
    return (
      <div style={{ minWidth: 520, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 100px', alignItems: 'center', gap: 8, padding: '8px 10px', background: erRed ? C.greenL : 'transparent', borderBottom: `1px solid ${C.border}`, borderRadius: erRed ? 6 : 0 }}>
        <div style={{ fontSize: 12 }}>{kat}</div>
        <div style={{ ...mono, fontSize: 12, textAlign: 'right' }}>{formatNOK(budj)}</div>
        {erRed ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', gridColumn: 'span 2' }}>
            <input type="number" value={editVal} onChange={e => setEditVal(e.target.value)} style={{ width: 100, padding: '4px 8px', borderRadius: 5, border: `1px solid ${C.green}`, fontFamily: "'DM Mono', monospace", fontSize: 12, textAlign: 'right', background: '#fff' }} autoFocus />
            <input placeholder="Notat" value={editNotat} onChange={e => setEditNotat(e.target.value)} style={{ flex: 1, padding: '4px 8px', borderRadius: 5, border: `1px solid ${C.border}`, fontSize: 12 }} />
          </div>
        ) : (
          <>
            <div style={{ ...mono, fontSize: 12, textAlign: 'right', color: f !== null ? C.text : C.muted, fontStyle: f !== null ? 'normal' : 'italic' }}>{f !== null ? formatNOK(f) : '—'}</div>
            <div style={{ ...mono, fontSize: 12, textAlign: 'right', color: avvik === null ? C.muted : avvikGod ? C.green : C.red }}>{avvik !== null ? formatNOK(avvik, true) : '—'}</div>
          </>
        )}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          {erRed ? (
            <>
              <button onClick={() => lagre(kat)} disabled={lagrer} style={{ background: C.green, color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>Lagre</button>
              <button onClick={() => setRedigerer(null)} style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 5, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}>✕</button>
            </>
          ) : (
            <button onClick={() => startRediger(kat, budj, f)} style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 5, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>Rediger</button>
          )}
        </div>
      </div>
    );
  };

  const TotalRad = ({ lbl, budj, kat_obj, erInntekt }) => {
    // Tell faktiske tall og budsjett separat
    let faktHarData = false;
    const faktSum = Object.entries(kat_obj).reduce((s, [k]) => {
      const f = getFaktisk(k, valgt);
      if (f !== null) { faktHarData = true; return s + f; }
      return s + (kat_obj[k].budsjett[valgt] || 0);
    }, 0);
    const reellFaktSum = Object.entries(kat_obj).reduce((s, [k]) => {
      const f = getFaktisk(k, valgt);
      return s + (f !== null ? f : 0);
    }, 0);
    const avvik = faktHarData ? reellFaktSum - budj : null;
    return (
      <div style={{ minWidth: 520, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 100px', gap: 8, padding: '9px 10px', background: C.surface2, borderRadius: 6, marginTop: 2 }}>
        <div style={{ fontSize: 12, fontWeight: 600 }}>{lbl}</div>
        <div style={{ ...mono, fontSize: 12, fontWeight: 600, textAlign: 'right' }}>{formatNOK(budj)}</div>
        <div style={{ ...mono, fontSize: 12, fontWeight: 600, textAlign: 'right', color: faktHarData ? C.text : C.muted, fontStyle: faktHarData ? 'normal' : 'italic' }}>
          {faktHarData ? formatNOK(reellFaktSum) : '—'}
        </div>
        <div style={{ ...mono, fontSize: 12, fontWeight: 600, textAlign: 'right', color: avvik === null ? C.muted : (erInntekt ? avvik >= 0 : avvik <= 0) ? C.green : C.red }}>
          {avvik !== null ? formatNOK(avvik, true) : '—'}
        </div>
        <div />
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px', margin: 0 }}>
          {MAANED_KORT[valgt]}
          {erGjennomfort && <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 400, background: C.greenL, color: C.green, padding: '2px 8px', borderRadius: 20 }}>Gjennomført</span>}
        </h1>
        <div className="month-scroll" style={isMobile ? undefined : { flexWrap: 'wrap' }}>
          {MAANED_KORT.map((m, i) => (
            <button key={i} onClick={() => setValgt(i)} style={{ padding: '8px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', background: valgt === i ? C.dark : i < gjennomfortTom ? C.greenL : C.surface, color: valgt === i ? '#fff' : i < gjennomfortTom ? C.green : C.muted, border: `1px solid ${valgt === i ? C.dark : C.border}`, whiteSpace: 'nowrap', flexShrink: 0, minHeight: 36 }}>{m}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
        {[
          { lbl: 'Inntekter', val: innFakt, budj: innSum, col: C.green },
          { lbl: 'Faste utgifter', val: fastFakt, budj: fastSum, col: C.blue },
          { lbl: 'Kredittkort', val: kkFakt, budj: kkSum, col: C.amber },
          { lbl: 'Netto', val: nettoFakt, budj: nettoVal, col: nettoFakt >= 0 ? C.green : C.red },
        ].map((k, i) => (
          <div key={i} style={{ ...card(), borderTop: `3px solid ${k.col}` }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>{k.lbl}</div>
            <div style={{ ...mono, fontSize: 18, fontWeight: 600, color: k.col }}>{formatNOK(k.val)}</div>
            <div style={{ ...mono, fontSize: 10, color: C.muted, marginTop: 3 }}>budsjett {formatNOK(k.budj)}</div>
          </div>
        ))}
      </div>
      <div className="scroll-x">
      <div style={{ minWidth: 520, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 100px', gap: 8, padding: '6px 10px', marginBottom: 4 }}>
        {['Post', 'Budsjett', 'Faktisk', 'Avvik', ''].map((h, i) => <div key={i} style={{ fontSize: 10, fontWeight: 600, color: C.muted, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: i > 0 && i < 4 ? 'right' : 'left' }}>{h}</div>)}
      </div>
      <div style={{ ...card(), marginBottom: 10, padding: '4px 0' }}>
        <div style={{ padding: '8px 10px 4px', fontSize: 11, fontWeight: 600, color: C.green, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Inntekter</div>
        {Object.entries(INNTEKTER).map(([kat, v]) => <RenderRad key={kat} kat={kat} budj={v.budsjett[valgt]} erInntekt />)}
        <TotalRad lbl="Sum inntekter" budj={innSum} kat_obj={Object.fromEntries(Object.entries(INNTEKTER).map(([k, v]) => [k, v]))} erInntekt />
      </div>
      <div style={{ ...card(), marginBottom: 10, padding: '4px 0' }}>
        <div style={{ padding: '8px 10px 4px', fontSize: 11, fontWeight: 600, color: C.blue, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Faste utgifter — bankkonto</div>
        {Object.entries(FASTE_UTGIFTER).map(([kat, v]) => <RenderRad key={kat} kat={kat} budj={v.budsjett[valgt]} />)}
        <TotalRad lbl="Sum faste utgifter" budj={fastSum} kat_obj={Object.fromEntries(Object.entries(FASTE_UTGIFTER).map(([k, v]) => [k, v]))} />
      </div>
      <div style={{ ...card(), padding: '4px 0' }}>
        <div style={{ padding: '8px 10px 4px', fontSize: 11, fontWeight: 600, color: C.amber, letterSpacing: '0.06em', textTransform: 'uppercase' }}>DNB kredittkort</div>
        {Object.entries(KREDITTKORT).map(([kat, v]) => <RenderRad key={kat} kat={kat} budj={v.budsjett[valgt]} />)}
        <TotalRad lbl="Sum kredittkort" budj={kkSum} kat_obj={Object.fromEntries(Object.entries(KREDITTKORT).map(([k, v]) => [k, v]))} />
      </div>
      </div>{/* /scroll-x */}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// KATEGORIER
// ══════════════════════════════════════════════════════════════════════════
function Kategorier({ token, faktisk, getFaktisk, gjennomfortTom }) {
  const [valgt, setValgt] = useState('Boliglån — renter');
  const [visTransaksjoner, setVisTransaksjoner] = useState(false);
  const alleKat = { ...Object.fromEntries(Object.entries(INNTEKTER).map(([k, v]) => [k, { ...v, gruppe: 'Inntekter' }])), ...Object.fromEntries(Object.entries(FASTE_UTGIFTER).map(([k, v]) => [k, { ...v, gruppe: 'Faste' }])), ...Object.fromEntries(Object.entries(KREDITTKORT).map(([k, v]) => [k, { ...v, gruppe: 'Kredittkort' }])) };
  const info = alleKat[valgt];
  const data = MAANEDER.map((m, i) => { const f = getFaktisk(valgt, i); return { name: MAANED_KORT[i], budsjett: info.budsjett[i], faktisk: f !== null ? f : undefined, gjennomfort: i < gjennomfortTom }; });
  const totBudj = info.budsjett.reduce((a, b) => a + b, 0);
  const faktData = data.filter(d => d.faktisk !== undefined);
  const totFakt = faktData.reduce((s, d) => s + d.faktisk, 0);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px', margin: 0 }}>Kategorier</h1>
        <select value={valgt} onChange={e => setValgt(e.target.value)} style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, background: C.surface, color: C.text, cursor: 'pointer' }}>
          <optgroup label="Inntekter">{Object.keys(INNTEKTER).map(k => <option key={k}>{k}</option>)}</optgroup>
          <optgroup label="Faste utgifter">{Object.keys(FASTE_UTGIFTER).map(k => <option key={k}>{k}</option>)}</optgroup>
          <optgroup label="Kredittkort">{Object.keys(KREDITTKORT).map(k => <option key={k}>{k}</option>)}</optgroup>
        </select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 12 }}>
        <div style={card()}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 14 }}>
            <span onClick={() => setVisTransaksjoner(true)} style={{ color: C.blue, cursor: 'pointer', textDecoration: 'underline' }} title="Vis transaksjoner">{valgt}</span> — månedsoversikt
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} barSize={22} barGap={4}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} tickFormatter={v => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v, n) => [formatNOK(v), n === 'budsjett' ? 'Budsjett' : 'Faktisk']} />
              <Bar dataKey="budsjett" fill={C.border} radius={[3, 3, 0, 0]} name="budsjett" />
              <Bar dataKey="faktisk" radius={[3, 3, 0, 0]} name="faktisk">{data.map((d, i) => <Cell key={i} fill={C.green} opacity={d.gjennomfort ? 1 : 0.5} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={card()}><div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Årsbudsjett</div><div style={{ ...mono, fontSize: 18, fontWeight: 600 }}>{formatNOK(totBudj)}</div></div>
          {faktData.length > 0 && <div style={card()}><div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Faktisk hittil ({faktData.length} mnd)</div><div style={{ ...mono, fontSize: 18, fontWeight: 600, color: C.green }}>{formatNOK(totFakt)}</div></div>}
          <div style={card()}><div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>Snitt/mnd</div><div style={{ ...mono, fontSize: 15 }}>Budj: {formatNOK(Math.round(totBudj / 12))}</div>{faktData.length > 0 && <div style={{ ...mono, fontSize: 15, color: C.green, marginTop: 4 }}>Fakt: {formatNOK(Math.round(totFakt / faktData.length))}</div>}</div>
          <div style={{ ...card(), fontSize: 12, color: C.muted, lineHeight: 1.6, fontStyle: 'italic' }}>{info.note}</div>
        </div>
      </div>
      {visTransaksjoner && <KategoriTransaksjoner token={token} kategori={valgt} onLukk={() => setVisTransaksjoner(false)} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// ÅRSPROGNOSE
// ══════════════════════════════════════════════════════════════════════════
function Aarsprognose({ faktisk: _faktisk, getFaktisk, gjennomfortTom }) {
  const isMobile = useIsMobile();
  const [visHittil, setVisHittil] = useState(false);

  // Månedlige verdier — Object.entries sikrer korrekt nøkkeloppslag
  const maanedVerdier = MAANEDER.map((m, i) => {
    const innM  = Object.entries(INNTEKTER).reduce((s, [k, v]) => { const f = getFaktisk(k, i); return s + (f !== null ? f : v.budsjett[i]); }, 0);
    const fastM = Object.entries(FASTE_UTGIFTER).reduce((s, [k, v]) => { const f = getFaktisk(k, i); return s + (f !== null ? f : v.budsjett[i]); }, 0);
    const kkM   = Object.entries(KREDITTKORT).reduce((s, [k, v]) => { const f = getFaktisk(k, i); return s + (f !== null ? f : v.budsjett[i]); }, 0);
    return { inn: Math.round(innM), fast: Math.round(fastM), kk: Math.round(kkM), netto: Math.round(innM - fastM - kkM) };
  });

  // Kumulativ sum (akkumulert netto måned for måned)
  const kumulativ = maanedVerdier.map((mv, i) => ({
    name: MAANED_KORT[i],
    kumulativ: maanedVerdier.slice(0, i + 1).reduce((s, v) => s + v.netto, 0),
    gjennomfort: i < gjennomfortTom,
  }));

  // Årstotaler
  const innProg  = maanedVerdier.reduce((s, v) => s + v.inn,  0);
  const fastProg = maanedVerdier.reduce((s, v) => s + v.fast, 0);
  const kkProg   = maanedVerdier.reduce((s, v) => s + v.kk,   0);
  const nettoProg = innProg - fastProg - kkProg;
  const innBudj  = budsjettAar(INNTEKTER), fastBudj = budsjettAar(FASTE_UTGIFTER), kkBudj = budsjettAar(KREDITTKORT);
  const nettoBudj = innBudj - fastBudj - kkBudj;

  // Hittil i år — kun gjennomførte måneder
  const hm = gjennomfortTom;
  const htInn  = maanedVerdier.slice(0, hm).reduce((s, v) => s + v.inn,  0);
  const htFast = maanedVerdier.slice(0, hm).reduce((s, v) => s + v.fast, 0);
  const htKK   = maanedVerdier.slice(0, hm).reduce((s, v) => s + v.kk,   0);
  const htNetto = htInn - htFast - htKK;
  const htBudjInn  = Object.values(INNTEKTER).reduce((s, v)      => s + v.budsjett.slice(0, hm).reduce((a, b) => a + b, 0), 0);
  const htBudjFast = Object.values(FASTE_UTGIFTER).reduce((s, v) => s + v.budsjett.slice(0, hm).reduce((a, b) => a + b, 0), 0);
  const htBudjKK   = Object.values(KREDITTKORT).reduce((s, v)    => s + v.budsjett.slice(0, hm).reduce((a, b) => a + b, 0), 0);
  const htBudjNetto = htBudjInn - htBudjFast - htBudjKK;

  // Per-kategori årstotaler til mobilkort
  const katTotaler = (kat, v) => {
    let faktiskSum = 0, prognoseSum = 0;
    for (let i = 0; i < 12; i++) {
      const f = getFaktisk(kat, i);
      if (f !== null) { faktiskSum += f; prognoseSum += f; }
      else { prognoseSum += v.budsjett[i]; }
    }
    const budsjettSum = v.budsjett.reduce((s, b) => s + b, 0);
    return { budsjett: budsjettSum, faktisk: faktiskSum, prognose: prognoseSum, avvik: prognoseSum - budsjettSum };
  };

  const renderKatKort = (kat, v, erInntekt) => {
    const d = katTotaler(kat, v);
    // Inntekter: positivt avvik = mer enn budsjett = grønn
    // Utgifter:  negativt avvik = mindre enn budsjett = grønn
    const positivErGod = erInntekt ? d.avvik > 0 : d.avvik < 0;
    const avvikKol = d.avvik === 0 ? C.muted : positivErGod ? C.green : C.red;
    const avvikBg  = d.avvik === 0 ? C.surface2 : positivErGod ? C.greenL : C.redL;
    return (
      <div key={kat} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 10 }}>{kat}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
          {[['Budsjett', d.budsjett], ['Faktisk', d.faktisk], ['Prognose', d.prognose]].map(([lbl, val]) => (
            <div key={lbl} style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', color: C.muted, letterSpacing: '0.04em', marginBottom: 2 }}>{lbl}</div>
              <div style={{ ...mono, fontSize: 13 }}>{formatNOK(val)}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <span style={{ ...mono, fontSize: 11, background: avvikBg, color: avvikKol, padding: '3px 10px', borderRadius: 20, fontWeight: 600 }}>
            {formatNOK(d.avvik, true)}
          </span>
        </div>
      </div>
    );
  };

  const renderSeksjonHeader = (label, farge) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20, marginBottom: 10 }}>
      <div style={{ width: 3, height: 16, background: farge, borderRadius: 2, flexShrink: 0 }} />
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
    </div>
  );

  // Sammendragskort med korrekt fargesemantikk
  const sammendragKort = [
    { lbl: 'Inntekter',   budj: innBudj,           prog: innProg,           col: C.green, positivErGod: true  },
    { lbl: 'Utgifter',    budj: fastBudj + kkBudj,  prog: fastProg + kkProg, col: C.blue,  positivErGod: false },
    { lbl: 'Årsresultat', budj: nettoBudj,          prog: nettoProg,         col: nettoProg >= 0 ? C.green : C.red, positivErGod: true },
  ];

  return (
    <div>
      {/* Topplinje: tittel + Hittil i år-knapp */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px', margin: 0 }}>Årsprognose</h1>
          <p style={{ color: C.muted, fontSize: 13, margin: '4px 0 0' }}>{gjennomfortTom} måneder gjennomført · {12 - gjennomfortTom} måneder budsjettert fremover</p>
        </div>
        <button
          onClick={() => setVisHittil(p => !p)}
          style={{ background: visHittil ? C.dark : C.surface, color: visHittil ? '#fff' : C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, alignSelf: isMobile ? 'stretch' : 'auto', textAlign: 'center' }}
        >
          {visHittil ? '✕ Lukk' : `📊 Hittil i år (jan–${MAANEDER[hm - 1].toLowerCase()})`}
        </button>
      </div>

      {/* Hittil i år — kompakt faktisk-oppsummering */}
      {visHittil && (
        <div style={{ ...card(), marginBottom: 16, borderLeft: `4px solid ${C.green}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
            Jan–{MAANEDER[hm - 1].toLowerCase()} {AAR} — faktiske tall
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap: 10 }}>
            {[
              { lbl: 'Inntekt',     val: htInn,           budj: htBudjInn,          peg: true  },
              { lbl: 'Utgifter',    val: htFast + htKK,   budj: htBudjFast + htBudjKK, peg: false },
              { lbl: 'Resultat',    val: htNetto,         budj: htBudjNetto,         peg: true  },
              { lbl: 'Vs. budsjett',val: htNetto - htBudjNetto, budj: null,          peg: true  },
            ].map(({ lbl, val, budj: b, peg }) => {
              const erAvvik = b === null;
              const kol = erAvvik ? (val === 0 ? C.muted : (val > 0) === peg ? C.green : C.red) : C.text;
              return (
                <div key={lbl} style={{ background: C.surface2, borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', color: C.muted, letterSpacing: '0.05em', marginBottom: 4 }}>{lbl}</div>
                  <div style={{ ...mono, fontSize: isMobile ? 13 : 15, fontWeight: 700, color: kol }}>{formatNOK(val, erAvvik)}</div>
                  {b !== null && <div style={{ ...mono, fontSize: 10, color: C.muted, marginTop: 2 }}>budsjett {formatNOK(b)}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sammendragskort: Inntekter / Utgifter / Årsresultat */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: isMobile ? 8 : 12, marginBottom: 16 }}>
        {sammendragKort.map((k, i) => {
          const diff = k.prog - k.budj;
          // Inntekter/Årsresultat: diff > 0 = grønn. Utgifter: diff > 0 = rød.
          const avvikKol = diff === 0 ? C.muted
            : k.positivErGod ? (diff > 0 ? C.green : C.red)
            : (diff > 0 ? C.red : C.green);
          return (
            <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: isMobile ? '10px 8px' : '18px 20px', borderTop: `3px solid ${k.col}` }}>
              <div style={{ fontSize: isMobile ? 9 : 11, color: C.muted, marginBottom: isMobile ? 4 : 6 }}>{k.lbl}</div>
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 9, color: C.muted }}>Budsjett</div>
                <div style={{ ...mono, fontSize: isMobile ? 11 : 15, fontWeight: 600 }}>{formatNOK(k.budj)}</div>
              </div>
              <div style={{ marginBottom: isMobile ? 4 : 8 }}>
                <div style={{ fontSize: 9, color: C.muted }}>Prognose</div>
                <div style={{ ...mono, fontSize: isMobile ? 11 : 15, fontWeight: 600, color: k.col }}>{formatNOK(k.prog)}</div>
              </div>
              <div style={{ ...mono, fontSize: isMobile ? 9 : 11, textAlign: 'right', color: avvikKol }}>
                {formatNOK(diff, true)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Kumulativ linjegraf */}
      <div style={{ ...card(), marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 14 }}>Kumulativt netto gjennom året</div>
        <ResponsiveContainer width="100%" height={isMobile ? 150 : 200}>
          <LineChart data={kumulativ}>
            <XAxis dataKey="name" tick={{ fontSize: isMobile ? 9 : 10, fill: C.muted }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} tickFormatter={v => `${Math.round(v / 1000)}k`} width={36} />
            <Tooltip formatter={v => [formatNOK(v), 'Kumulativt netto']} />
            <ReferenceLine y={0} stroke={C.border} />
            <Line type="monotone" dataKey="kumulativ" stroke={C.green} strokeWidth={2} dot={({ cx, cy, payload }) => <circle key={cx} cx={cx} cy={cy} r={4} fill={payload.gjennomfort ? C.green : C.surface} stroke={C.green} strokeWidth={2} />} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Mobil: kortbasert kategorivis / Desktop: månedstabell */}
      {isMobile ? (
        <div>
          {renderSeksjonHeader('Inntekter', C.green)}
          {Object.entries(INNTEKTER).map(([kat, v]) => renderKatKort(kat, v, true))}
          {renderSeksjonHeader('Faste utgifter', C.blue)}
          {Object.entries(FASTE_UTGIFTER).map(([kat, v]) => renderKatKort(kat, v, false))}
          {renderSeksjonHeader('Kredittkort', C.amber)}
          {Object.entries(KREDITTKORT).map(([kat, v]) => renderKatKort(kat, v, false))}
        </div>
      ) : (
        <div style={card()}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10 }}>Månedsoversikt — budsjett vs. prognose</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', fontSize: 10, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
            {['Måned', 'Inntekt', 'Utgifter', 'Netto', 'Status'].map((h, i) => <div key={i} style={{ textAlign: i > 0 ? 'right' : 'left' }}>{h}</div>)}
          </div>
          {maanedVerdier.map((mv, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', padding: '7px 0', borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : C.surface2 }}>
              <div style={{ fontSize: 12 }}>{MAANED_KORT[i]}{i < gjennomfortTom && <span style={{ marginLeft: 6, fontSize: 9, color: C.green, background: C.greenL, padding: '1px 5px', borderRadius: 3 }}>✓</span>}</div>
              <div style={{ ...mono, fontSize: 12, textAlign: 'right', color: C.green }}>{formatNOK(mv.inn)}</div>
              <div style={{ ...mono, fontSize: 12, textAlign: 'right' }}>{formatNOK(mv.fast + mv.kk)}</div>
              <div style={{ ...mono, fontSize: 12, textAlign: 'right', color: mv.netto >= 0 ? C.green : C.red, fontWeight: 500 }}>{formatNOK(mv.netto, true)}</div>
              <div style={{ textAlign: 'right', fontSize: 11 }}><span style={{ background: i < gjennomfortTom ? C.greenL : C.surface2, color: i < gjennomfortTom ? C.green : C.muted, padding: '2px 8px', borderRadius: 20 }}>{i < gjennomfortTom ? 'Gjennomført' : 'Budsjett'}</span></div>
            </div>
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', padding: '9px 0', background: C.dark, borderRadius: 6, marginTop: 4 }}>
            {['Totalt', formatNOK(innProg), formatNOK(fastProg + kkProg), formatNOK(nettoProg, true), ''].map((v, i) =>
              <div key={i} style={{ ...mono, fontSize: 12, fontWeight: 600, color: '#fff', textAlign: i > 0 ? 'right' : 'left', padding: '0 4px' }}>{v}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// BANK-SYNK
// ══════════════════════════════════════════════════════════════════════════
function BankSynk({ token, lastFaktisk }) {
  const FASTE_KAT = ['Boliglån — renter','Billeasing (DNB Finans)','Forsikring','Strøm','Kommunale avgifter','Internett / mobil','Studielån','Vask / renhold','Barn og familie','Nordnet — sparing','Molde Båthavn','Lønn Terje','Lønn Elin','Jobbutlegg Terje','Skatt / offentlig','Diverse','Vedlikehold hus','Vedlikehold hytte'];
  const ALLE_KAT = [...FASTE_KAT, ...KK_KATEGORIER, 'Diverse', 'Ignorer'];

  const [synkData, setSynkData] = useState(null);
  const [laster, setLaster] = useState(false);
  const [melding, setMelding] = useState(null);
  const [valgtKonto, setValgtKonto] = useState('');
  const [redigererKat, setRedigererKat] = useState(null);
  const [valgtMaaned, setValgtMaaned] = useState(new Date().getMonth() + 1);
  const [synkVarsler, setSynkVarsler] = useState([]);

  useEffect(() => {
    api.hentLagrede(token).then(res => {
      if (res.ok) {
        setSynkData(res);
        setMelding({ type: 'ok', tekst: `Viser ${res.antall} lagrede transaksjoner (hentet ${new Date(res.hentet).toLocaleDateString('nb-NO')})` });
      }
    });
    api.hentSynkVarsler(token).then(res => setSynkVarsler(res?.varsler || []));
  }, [token]);

  const hentTransaksjoner = async () => {
    setLaster(true);
    setMelding(null);
    const res = await api.bankSynk(token, valgtKonto || null);
    setLaster(false);
    if (res.ok) {
      setSynkData(res);
      setMelding({ type: 'ok', tekst: `Hentet ${res.antall} transaksjoner` });
    } else {
      setMelding({ type: 'feil', tekst: res.error || 'Feil ved henting' });
    }
  };

  const endreKategori = async (idx, nyKategori) => {
    const tx = synkData.transaksjoner[idx];
    setSynkData(prev => {
      const nye = [...prev.transaksjoner];
      nye[idx] = { ...nye[idx], kategori: nyKategori };
      return { ...prev, transaksjoner: nye };
    });
    if (tx.beskrivelse && tx.beskrivelse !== 'Betaling' && tx.beskrivelse !== 'Overføring') {
      try {
        const res = await api.lagreKategoriLæring(token, tx.beskrivelse, nyKategori);
        if (res.ok && !res.ignorert) setMelding({ type: 'ok', tekst: `Husker: ${tx.beskrivelse} → ${nyKategori} (gjelder fra neste synk)` });
        else if (!res.ok) setMelding({ type: 'feil', tekst: 'Kategori endret, men læring ble ikke lagret.' });
      } catch {
        setMelding({ type: 'feil', tekst: 'Kategori endret, men læring ble ikke lagret (nettverksfeil).' });
      }
    }
  };

  const kjørAutoSynk = async () => {
    setLaster(true);
    setMelding(null);
    const res = await api.autoSynk(token);
    setLaster(false);
    if (res.ok) {
      setMelding({ type: 'ok', tekst: `✓ Auto-synk ferdig! ${res.kategorier} kategorier oppdatert fra ${res.antall} transaksjoner.` });
      await lastFaktisk();
      api.hentSynkVarsler(token).then(v => setSynkVarsler(v?.varsler || []));
    } else {
      setMelding({ type: 'feil', tekst: res.error || 'Feil ved auto-synk' });
    }
  };

  const vistetransaksjoner = synkData?.transaksjoner.filter(t => t.maaned === valgtMaaned && (!valgtKonto || t.konto === valgtKonto)) || [];
  const utgifter = vistetransaksjoner.filter(t => t.retning === 'ut' && t.kategori !== 'Ignorer');
  const inntekter = vistetransaksjoner.filter(t => t.retning === 'inn' && t.kategori !== 'Ignorer');
  const totalUt = utgifter.reduce((s, t) => s + t.beloep, 0);
  const totalInn = inntekter.reduce((s, t) => s + t.beloep, 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px', margin: 0 }}>Bankkonto</h1>
          <p style={{ color: C.muted, fontSize: 13, margin: '4px 0 0' }}>Hent og kategoriser transaksjoner fra DNB via Enable Banking</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {synkData?.kontoer && (
            <select value={valgtKonto} onChange={e => { setValgtKonto(e.target.value); }}
              style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, background: C.surface }}>
              <option value="">Alle kontoer</option>
              {synkData.kontoer.map(k => (
                <option key={k.iban} value={k.iban}>{k.navn} ({k.iban?.slice(-4)})</option>
              ))}
            </select>
          )}
          <button onClick={kjørAutoSynk} disabled={laster} style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
            {laster ? 'Synker...' : '⚡ Auto-synk'}
          </button>
          <button onClick={hentTransaksjoner} disabled={laster} style={{ background: C.dark, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
            {laster ? 'Henter...' : '↻ Hent fra bank'}
          </button>
        </div>
      </div>

      {synkVarsler.length > 0 && (
        <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14, background: C.amberL, borderLeft: `3px solid ${C.amber}`, color: C.amber }}>
          {synkVarsler.map((v, i) => <div key={i}>{v.tekst}</div>)}
        </div>
      )}

      {melding && (
        <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14, background: melding.type === 'ok' ? C.greenL : C.redL, borderLeft: `3px solid ${melding.type === 'ok' ? C.green : C.red}`, color: melding.type === 'ok' ? C.green : C.red }}>
          {melding.tekst}
        </div>
      )}

      {synkData && (
        <>
          {/* Månedvelger */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
            {MAANED_KORT.map((m, i) => {
              const harData = synkData.transaksjoner.some(t => t.maaned === i + 1);
              if (!harData) return null;
              return (
                <button key={i} onClick={() => setValgtMaaned(i + 1)} style={{ padding: '5px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', background: valgtMaaned === i + 1 ? C.dark : C.surface, color: valgtMaaned === i + 1 ? '#fff' : C.muted, border: `1px solid ${valgtMaaned === i + 1 ? C.dark : C.border}` }}>{m}</button>
              );
            })}
          </div>

          {/* KPI */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
            <div style={{ ...card(), borderTop: `3px solid ${C.red}` }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Totale utgifter</div>
              <div style={{ ...mono, fontSize: 20, fontWeight: 600, color: C.red }}>{formatNOK(totalUt)}</div>
            </div>
            <div style={{ ...card(), borderTop: `3px solid ${C.green}` }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Totale inntekter</div>
              <div style={{ ...mono, fontSize: 20, fontWeight: 600, color: C.green }}>{formatNOK(totalInn)}</div>
            </div>
            <div style={{ ...card(), borderTop: `3px solid ${C.blue}` }}>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Antall transaksjoner</div>
              <div style={{ ...mono, fontSize: 20, fontWeight: 600, color: C.blue }}>{vistetransaksjoner.length}</div>
            </div>
          </div>

          {/* Transaksjonsliste */}
          <div style={card()}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 12 }}>
              Transaksjoner — {MAANED_KORT[valgtMaaned - 1]}
            </div>
            <div className="scroll-x">
            <div style={{ minWidth: 480, display: 'grid', gridTemplateColumns: '80px 1fr 1.8fr 90px 80px', fontSize: 10, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '4px 6px', borderBottom: `1px solid ${C.border}` }}>
              {['Dato', 'Beskrivelse', 'Kategori', 'Beløp', 'Retning'].map((h, i) => (
                <div key={i} style={{ textAlign: i === 3 ? 'right' : 'left' }}>{h}</div>
              ))}
            </div>
            {vistetransaksjoner.map((t, i) => {
              const globalIdx = synkData.transaksjoner.indexOf(t);
              return (
                <div key={i} style={{ minWidth: 480, display: 'grid', gridTemplateColumns: '80px 1fr 1.8fr 90px 80px', alignItems: 'center', gap: 6, padding: '7px 6px', borderBottom: `1px solid ${C.border}`, background: t.kategori === 'Ignorer' ? C.surface2 : i % 2 === 0 ? 'transparent' : '#fafaf8', opacity: t.kategori === 'Ignorer' ? 0.5 : 1 }}>
                  <div style={{ fontSize: 11, color: C.muted }}>{t.dato?.slice(5).replace('-', '.')}</div>
                  <div style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.beskrivelse}>{t.beskrivelse}</div>
                  <select value={t.kategori} onChange={e => endreKategori(globalIdx, e.target.value)}
                    style={{ padding: '2px 6px', borderRadius: 5, border: `1px solid ${C.border}`, fontSize: 11, background: '#fff', cursor: 'pointer', width: '100%' }}>
                    <option value="Ignorer">— Ignorer —</option>
                    <optgroup label="Inntekter">
                      {['Terje — DSD Explore','Elin — Hustadvika','Elin — Helse M&R','Husleie'].map(k => <option key={k} value={k}>{k}</option>)}
                    </optgroup>
                    <optgroup label="Faste utgifter">{FASTE_KAT.map(k => <option key={k} value={k}>{k}</option>)}</optgroup>
                    <optgroup label="Kredittkort">{KK_KATEGORIER.map(k => <option key={k} value={k}>{k}</option>)}</optgroup>
                    <option value="Diverse">Diverse</option>
                  </select>
                  <div style={{ ...mono, fontSize: 12, fontWeight: 500, textAlign: 'right', color: t.retning === 'inn' ? C.green : C.text }}>{formatNOK(t.beloep)}</div>
                  <div style={{ textAlign: 'right', fontSize: 11 }}>
                    <span style={{ background: t.retning === 'inn' ? C.greenL : C.redL, color: t.retning === 'inn' ? C.green : C.red, padding: '2px 6px', borderRadius: 10 }}>
                      {t.retning === 'inn' ? 'inn' : 'ut'}
                    </span>
                  </div>
                </div>
              );
            })}
            {vistetransaksjoner.length === 0 && (
              <div style={{ textAlign: 'center', padding: 24, color: C.muted, fontSize: 13 }}>Ingen transaksjoner for denne måneden</div>
            )}
            </div>{/* /scroll-x */}
          </div>

          <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: C.amberL, borderLeft: `3px solid ${C.amber}`, fontSize: 12, color: C.amber }}>
            <strong>Tips:</strong> Endre kategori ved å klikke på nedtrekkslisten. Velg "Ignorer" for overføringer mellom egne kontoer. Endringer huskes til neste gang.
          </div>
        </>
      )}

      {!synkData && !laster && (
        <div style={{ ...card(), textAlign: 'center', padding: 40, color: C.muted }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🏦</div>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>Hent transaksjoner fra DNB</div>
          <div style={{ fontSize: 12 }}>Trykk "Hent fra bank" for å se og kategorisere banktransaksjonene dine</div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════
// DnB-KOBLING
// ══════════════════════════════════════════════════════════════════════════
function BankKobling({ token }) {
  const [status, setStatus] = useState(null);
  const [transaksjoner, setTransaksjoner] = useState([]);
  const [saldo, setSaldo] = useState([]);
  const [laster, setLaster] = useState(false);
  const [melding, setMelding] = useState(null);
  const headers = { 'x-token': token, 'Content-Type': 'application/json' };
  const lastStatus = async () => { const r = await fetch('/api/bank/status', { headers }); setStatus(await r.json()); };
  useEffect(() => {
    lastStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('bank_connected')) { setMelding({ type: 'ok', tekst: 'DnB koblet til!' }); window.history.replaceState({}, '', window.location.pathname); }
    if (params.get('bank_error')) { setMelding({ type: 'feil', tekst: 'Feil: ' + params.get('bank_error') }); window.history.replaceState({}, '', window.location.pathname); }
  }, []);
  const kobbilTilDnb = async () => {
    setLaster(true);
    const r = await fetch('/api/bank/connect', { method: 'POST', headers, body: JSON.stringify({ aspspId: 'DNB' }) });
    const d = await r.json();
    if (d.url) window.location.href = d.url;
    else setMelding({ type: 'feil', tekst: d.error });
    setLaster(false);
  };
  const hentTransaksjoner = async () => {
    setLaster(true);
    const r = await fetch('/api/bank/transaksjoner', { headers });
    const d = await r.json();
    if (Array.isArray(d)) { setTransaksjoner(d); setMelding({ type: 'ok', tekst: `Hentet ${d.length} transaksjoner` }); }
    else setMelding({ type: 'feil', tekst: d.error });
    setLaster(false);
  };
  const hentSaldo = async () => { const r = await fetch('/api/bank/saldo', { headers }); const d = await r.json(); if (Array.isArray(d)) setSaldo(d); };
  const kobblaFra = async () => { await fetch('/api/bank/disconnect', { method: 'POST', headers }); setStatus({ koblet: false }); setTransaksjoner([]); setSaldo([]); };
  const erKoblet = status?.koblet;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px', margin: 0 }}>DnB-kobling</h1>
          <p style={{ color: C.muted, fontSize: 13, margin: '4px 0 0' }}>Open Banking via Enable Banking / PSD2</p>
        </div>
        {erKoblet && <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={hentTransaksjoner} disabled={laster} style={{ background: C.green, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 12, cursor: 'pointer' }}>{laster ? 'Henter...' : 'Hent transaksjoner'}</button>
        </div>}
      </div>
      {melding && <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14, background: melding.type === 'ok' ? C.greenL : C.redL, borderLeft: `3px solid ${melding.type === 'ok' ? C.green : C.red}`, color: melding.type === 'ok' ? C.green : C.red }}>{melding.tekst}</div>}
      {!erKoblet && <div style={{ ...card(), textAlign: 'center', padding: 32 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 20 }}>Koble til DnB via BankID</div>
        <button onClick={kobbilTilDnb} disabled={laster} style={{ background: C.dark, color: '#fff', border: 'none', borderRadius: 8, padding: '12px 28px', fontSize: 14, cursor: 'pointer', fontWeight: 500 }}>{laster ? 'Kobler...' : 'Koble til DnB med BankID'}</button>
      </div>}
      {erKoblet && <div style={{ textAlign: 'right', marginBottom: 12 }}><button onClick={kobblaFra} style={{ background: 'transparent', color: C.red, border: `1px solid ${C.red}`, borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer' }}>Koble fra</button></div>}
      {transaksjoner.length > 0 && <div style={card()}>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 12 }}>Siste {transaksjoner.length} transaksjoner</div>
        <div className="scroll-x">
        {transaksjoner.slice(0, 50).map((tx, i) => (
          <div key={i} style={{ minWidth: 420, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '7px 0', borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : C.surface2, fontSize: 12 }}>
            <div style={{ color: C.muted }}>{tx.dato}</div>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{tx.beskrivelse}</div>
            <div style={{ color: C.muted, fontSize: 11 }}>{tx.kategori}</div>
            <div style={{ ...mono, textAlign: 'right', color: tx.retning === 'inn' ? C.green : C.text }}>{tx.retning === 'inn' ? '+' : '−'}{tx.beloep.toLocaleString('no-NO')} kr</div>
          </div>
        ))}
        </div>{/* /scroll-x */}
      </div>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// CHAT-FANE — spør om budsjettet
// ══════════════════════════════════════════════════════════════════════════
const CHAT_VELKOMST = { rolle: 'assistent', tekst: 'Hei! Jeg er din personlige familieøkonomiekspert. Jeg kjenner alle tallene dine for 2026 og kan gi deg konkrete råd. Hva vil du vite?' };

function ChatFane({ token }) {
  const isMobile = useIsMobile();
  const [meldinger, setMeldinger] = useState([CHAT_VELKOMST]);
  const [input, setInput] = useState('');
  const [laster, setLaster] = useState(false);
  const harSendt = React.useRef(false);

  // Last lagret samtale ved åpning — tråden lagres i budsjett.json
  React.useEffect(() => {
    fetch('/api/chat/traad', { headers: { 'x-token': token } })
      .then(r => r.json())
      .then(d => { if (Array.isArray(d.traad) && d.traad.length > 0) setMeldinger([CHAT_VELKOMST, ...d.traad]); })
      .catch(() => {});
  }, [token]);

  const nullstill = async () => {
    if (!window.confirm('Slette hele samtalen? Dette kan ikke angres.')) return;
    try {
      const r = await fetch('/api/chat/traad', { method: 'DELETE', headers: { 'x-token': token } }).then(r => r.json());
      if (r.ok) { setMeldinger([CHAT_VELKOMST]); harSendt.current = false; }
    } catch {}
  };
  // Ref på selve scroll-containeren — vi styrer scroll her, ikke på siden
  const scrollContainerRef = React.useRef(null);
  // Ref settes dynamisk på siste assistent-boble ved render
  const lastSvarRef = React.useRef(null);

  // Scroll inni containeren til siste svar-boble — aldri ved første render
  React.useEffect(() => {
    if (!harSendt.current) return;
    const sisteMelding = meldinger[meldinger.length - 1];
    if (sisteMelding?.rolle !== 'assistent') return;
    const container = scrollContainerRef.current;
    const el = lastSvarRef.current;
    if (container && el) {
      container.scrollTo({ top: el.offsetTop - 8, behavior: 'smooth' });
    }
  }, [meldinger]);

  const send = async () => {
    const tekst = input.trim();
    if (!tekst || laster) return;
    harSendt.current = true;
    setInput('');
    setMeldinger(prev => [...prev, { rolle: 'bruker', tekst }]);
    setLaster(true);
    try {
      const r = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-token': token },
        body: JSON.stringify({ sporsmal: tekst })
      }).then(r => r.json());
      if (Array.isArray(r.traad)) setMeldinger([CHAT_VELKOMST, ...r.traad]);
      else setMeldinger(prev => [...prev, { rolle: 'assistent', tekst: r.svar || r.error || 'Ukjent feil' }]);
    } catch {
      setMeldinger(prev => [...prev, { rolle: 'assistent', tekst: 'Kunne ikke nå serveren. Prøv igjen.' }]);
    }
    setLaster(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const containerHeight = isMobile
    ? 'calc(100dvh - 52px - 16px - 80px - env(safe-area-inset-bottom, 0px))'
    : 'calc(100vh - 52px - 56px)';

  // Indeks til siste assistent-melding — brukes til å feste ref i render
  const sisteAssistentIdx = meldinger.reduce((acc, m, i) => m.rolle === 'assistent' ? i : acc, -1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: containerHeight, minHeight: 320, overflow: 'hidden' }}>
      <div style={{ marginBottom: 12, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px', margin: 0 }}>Spør om budsjettet</h1>
          <p style={{ color: C.muted, fontSize: 13, margin: '4px 0 0' }}>AI-assistent med tilgang til alle faktiske tall for 2026 · samtalen lagres</p>
        </div>
        {meldinger.length > 1 && (
          <button onClick={nullstill} style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.muted, fontSize: 12, cursor: 'pointer', borderRadius: 6, padding: '6px 12px', flexShrink: 0 }}>
            🗑 Nullstill
          </button>
        )}
      </div>

      {/* Meldingshistorikk — scroll skjer KUN her inne, ikke på siden */}
      <div ref={scrollContainerRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 8 }}>
        {meldinger.map((m, i) => (
          <div
            key={i}
            ref={i === sisteAssistentIdx ? lastSvarRef : null}
            style={{ display: 'flex', justifyContent: m.rolle === 'bruker' ? 'flex-end' : 'flex-start' }}
          >
            <div style={{
              maxWidth: '85%',
              padding: '10px 14px',
              borderRadius: m.rolle === 'bruker' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              background: m.rolle === 'bruker' ? C.dark : C.surface,
              color: m.rolle === 'bruker' ? '#fff' : C.text,
              border: m.rolle === 'bruker' ? 'none' : `1px solid ${C.border}`,
              fontSize: 15,
              lineHeight: 1.55,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {m.tekst}
            </div>
          </div>
        ))}
        {laster && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ padding: '10px 16px', borderRadius: '16px 16px 16px 4px', background: C.surface, border: `1px solid ${C.border}`, color: C.muted, fontSize: 15 }}>
              Tenker…
            </div>
          </div>
        )}
      </div>

      {/* Inputfelt — alltid synlig, aldri autofokus */}
      <div style={{ display: 'flex', gap: 8, paddingTop: 10, flexShrink: 0, borderTop: `1px solid ${C.border}`, paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : 0 }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Spør om budsjettet… (Enter for å sende)"
          rows={2}
          style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 15, fontFamily: "'DM Sans', sans-serif", resize: 'none', outline: 'none', background: C.surface, minWidth: 0 }}
        />
        <button
          onClick={send}
          disabled={laster || !input.trim()}
          style={{ background: laster || !input.trim() ? C.surface2 : C.dark, color: laster || !input.trim() ? C.muted : '#fff', border: 'none', borderRadius: 8, padding: '0 18px', cursor: laster || !input.trim() ? 'default' : 'pointer', fontSize: 15, fontWeight: 500, minWidth: 64, minHeight: 44, flexShrink: 0 }}
        >
          Send
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// BANK-FANE (slår sammen KK-transaksjoner, Bankkonto og DnB-kobling)
// ══════════════════════════════════════════════════════════════════════════
function BankFane({ token, lastFaktisk, gjennomfortTom }) {
  const [underfane, setUnderfane] = useState('kk');
  return (
    <div>
      <div style={{ display: 'flex', gap: 2, marginBottom: 24, background: C.surface2, padding: 4, borderRadius: 10 }}>
        {[['kk', 'KK-transaksjoner'], ['banksynk', 'Bankkonto'], ['kobling', 'DNB-kobling']].map(([id, lbl]) => (
          <button key={id} onClick={() => setUnderfane(id)}
            style={{ flex: 1, padding: '10px 8px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: underfane === id ? 600 : 400, background: underfane === id ? C.dark : 'transparent', color: underfane === id ? '#fff' : C.muted, minHeight: 44, transition: 'background 0.15s' }}>
            {lbl}
          </button>
        ))}
      </div>
      {underfane === 'kk' && <PushTransaksjoner token={token} lastFaktisk={lastFaktisk} gjennomfortTom={gjennomfortTom} />}
      {underfane === 'banksynk' && <BankSynk token={token} lastFaktisk={lastFaktisk} />}
      {underfane === 'kobling' && <BankKobling token={token} />}
    </div>
  );
}
